import { useEffect, useState } from 'react'
import Header from './Header'
import Footer from './Footer'
import { supabase } from '../lib/supabase'
import { useNavigate, useLocation } from 'react-router-dom'
import { MAX_YEAR } from './constants.js'
import DocumentMeta from './DocumentMeta.jsx'
import { trackEvent } from './lib/analytics'
import { reportError } from './lib/reportError'

function ScoreCalculatorPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [fields, setFields] = useState([])
  const [selectedField, setSelectedField] = useState(null)
  const [subjects, setSubjects] = useState([])
  const [scores, setScores] = useState({})
  const [finalScore, setFinalScore] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [fieldsLoading, setFieldsLoading] = useState(true)
  const [subjectsLoading, setSubjectsLoading] = useState(false)

  const goToTargetsWithEstimate = () => {
    if (!selectedField || finalScore === null || finalScore === undefined) return
  
    const estimatedMoria = Math.floor(finalScore)

    trackEvent('scorecalc_view_targets_clicked', {
      field_id: selectedField,
      estimated_moria: estimatedMoria,
      year: MAX_YEAR,
    })

    // Persist estimate so Target Schools page can hydrate it on load.
    try {
      const storageKey = 'panellhnies_target_schools_v1'
      const raw = localStorage.getItem(storageKey)
      const parsed = raw ? JSON.parse(raw) : {}
      const safeTargets = Array.isArray(parsed?.targets) ? parsed.targets : []

      localStorage.setItem(
        storageKey,
        JSON.stringify({
          estimatedMoria,
          targets: safeTargets,
        })
      )
    } catch {
      // Ignore local storage errors and continue navigation.
    }

    navigate('/stoxoi-sxolon')
  }

  // Fetch all fields from Supabase
  useEffect(() => {
    const fetchFields = async () => {
      setFieldsLoading(true)
      const { data, error } = await supabase
        .from('fields')
        .select('id, number, name')
        .order('number', { ascending: true })
      if (error) {
        console.error(error)
        reportError(error, { where: 'scorecalc_fetch_fields' })
      } else {
        setFields(data || [])
      }
      setFieldsLoading(false)
    }
    fetchFields()
  }, [])  

  // Fetch subjects for selected field
  useEffect(() => {
    if (!selectedField) return
    setSubjectsLoading(true)
  
    const fetchSubjects = async () => {
      try {
        const { data, error } = await supabase
          .from('subject_fields')
          .select(`subjects!inner(id, name)`)
          .eq('field_id', selectedField)
  
        if (error) throw error
  
        const uniqueSubjects = []
        const seen = new Set()
        data.forEach(sf => {
          if (sf.subjects && !seen.has(sf.subjects.id)) {
            seen.add(sf.subjects.id)
            uniqueSubjects.push(sf.subjects)
          }
        })
  
        setSubjects(uniqueSubjects)
        setScores({})
        setFinalScore(null)
        setShowModal(false)
      } catch (err) {
        console.error(err)
        reportError(err, { where: 'scorecalc_fetch_subjects', selectedField })
        setSubjects([])
      } finally {
        setSubjectsLoading(false)
      }
    }
  
    fetchSubjects()
  }, [selectedField])
  

  const handleScoreChange = (subjectId, value) => {
    const parsed = parseFloat(value)
    setScores(prev => ({ ...prev, [subjectId]: isNaN(parsed) ? undefined : parsed }))
  }

  const calculateFinalScore = () => {
    const values = subjects.map(s => scores[s.id]).filter(v => typeof v === 'number')
    if (values.length !== subjects.length) return
    const total = values.reduce((sum, v) => sum + v, 0)
    const average = total / values.length

    trackEvent('scorecalc_calculated', {
      field_id: selectedField || null,
      subjects_count: subjects.length,
      score_floor: Math.floor(average),
    })
    setFinalScore(average)
    setShowModal(true)
  }

  return (
    <>
    <DocumentMeta
      title={`Υπολογισμός Μορίων Πανελληνίων ${MAX_YEAR} | Πανελλήνιες+`}
      description={`Υπολόγισε τα μόριά σου για τις Πανελλήνιες ${MAX_YEAR} ανά πεδίο και μαθήματα.`}
      canonical={`https://www.panellhniesplus.gr${location.pathname}`}
    />
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Αρχική", item: "https://www.panellhniesplus.gr/" },
          { "@type": "ListItem", position: 2, name: "Υπολογισμός Μορίων", item: "https://www.panellhniesplus.gr/ypologismos-morion" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        name: `Υπολογιστής Μορίων Πανελληνίων ${MAX_YEAR}`,
        url: "https://www.panellhniesplus.gr/ypologismos-morion",
        applicationCategory: "EducationalApplication",
        operatingSystem: "Web",
        description: `Υπολόγισε τα μόριά σου για τις Πανελλήνιες ${MAX_YEAR} ανά πεδίο και μαθήματα. Δωρεάν εργαλείο για μαθητές.`,
      },
    ]) }} />
    <div className="min-h-screen bg-white text-gray-900">
      <Header />

      <main className="max-w-3xl mx-auto p-6">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold tracking-tight">Υπολογισμός Μορίων</h1>
        </div>

        {/* Field Select */}
        <div className="mb-10">
          {fieldsLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-500"></div>
            </div>
          ) : (
          <>
            <label htmlFor="field-select" className="block text-sm font-medium text-zinc-700 mb-2">
              Επιλέξτε Πεδίο
            </label>
            <select
            id="field-select"
            className="w-full cursor-pointer rounded-xl border border-gray-200 bg-white text-zinc-900 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gray-400"
            value={selectedField || ''}
            onChange={e => setSelectedField(e.target.value)}
          >
            <option value="">— Επιλέξτε —</option>
            {fields.map(field => (
              <option key={field.id} value={field.id}>
                {field.number} — {field.name}
              </option>
            ))}
          </select>
          </>
          )}
        </div>

        {subjectsLoading && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-500"></div>
          </div>
        )}

        {/* Subject Inputs */}
        {!subjectsLoading && subjects.length > 0 && (
          <form
            onSubmit={e => {
              e.preventDefault()
              calculateFinalScore()
            }}
            className="bg-white shadow rounded-2xl p-6 border border-gray-100"
          >
            <h2 className="text-xl font-semibold text-zinc-800 mb-6">Βαθμοί Μαθημάτων</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {subjects.map(s => (
                <div key={s.id}>
                  <label htmlFor={`subject-${s.id}`} className="block text-sm font-medium text-zinc-600 mb-1">
                    {s.name}
                  </label>
                  <input
                    id={`subject-${s.id}`}
                    type="number"
                    min="0"
                    max="20000"
                    step="1"
                    required
                    onChange={e => handleScoreChange(s.id, e.target.value)}
                    className="w-full rounded-xl border border-zinc-300 bg-zinc-50 px-4 py-2 text-base h-11 text-zinc-800 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  />
                  <p className="mt-1 text-xs text-zinc-500">0 – 20000</p>
                </div>
              ))}
            </div>

            <div className="mt-6 text-right">
              <button
                type="submit"
                className="bg-gray-900 cursor-pointer hover:bg-gray-800 text-white font-semibold px-6 py-3 rounded-xl transition-all"
              >
                Υπολογισμός
              </button>
            </div>
          </form>
        )}
      </main>

      {/* Result Modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-md"
          role="dialog"
          aria-modal="true"
          aria-labelledby="result-title"
        >
          <div className="relative bg-white text-zinc-800 rounded-2xl shadow-2xl px-6 sm:px-8 py-7 w-full max-w-md border border-gray-200">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-3 right-4 text-zinc-400 hover:text-gray-700 text-2xl font-bold focus:outline-none cursor-pointer"
              aria-label="Κλείσιμο"
            >
              &times;
            </button>

            <h3 id="result-title" className="text-xl sm:text-2xl font-extrabold text-gray-900">
              Αποτέλεσμα
            </h3>

            <p className="mt-2 text-sm text-gray-600">
              Με βάση τους βαθμούς που έβαλες, τα μόριά σου είναι:
            </p>

            <div className="mt-5 rounded-2xl border border-gray-200 bg-gray-50 px-5 py-4">
              <div className="text-xs font-semibold text-gray-500">Μόρια</div>
              <div className="mt-1 text-4xl font-extrabold text-gray-900 leading-none">
                {Math.floor(finalScore).toLocaleString('el-GR')}
              </div>
            </div>

            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <button
                onClick={goToTargetsWithEstimate}
                className="w-full cursor-pointer bg-gray-900 hover:bg-gray-800 text-white font-semibold px-5 py-3 rounded-xl transition"
              >
                Πήγαινε στους στόχους σου
              </button>

              <button
                onClick={() => setShowModal(false)}
                className="w-full cursor-pointer bg-white text-gray-900 font-semibold px-5 py-3 rounded-xl border border-gray-300 hover:bg-gray-50 transition"
              >
                Κλείσιμο
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
    </>
  )
}

export default ScoreCalculatorPage
